import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { dashboardAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, MapPin, Pencil, Wind } from 'lucide-react';

// Light-mode classes ONLY, on purpose. The palette in styles/palette.css
// INVERTS each colour ramp under [data-theme="dark"] — amber-50 becomes dark
// and amber-900 becomes light — so these pairings stay legible in both themes
// on their own. Adding `dark:` overrides re-inverts them: that is what made
// these advisories a muddy tan block with brown text on the dark dashboard
// (reported 2026-09-08). Never add a `dark:` colour override to a ramp colour.
const SEVERITY_STYLE = {
  Extreme: 'bg-red-50 text-red-900 border-red-400',
  Severe: 'bg-red-50 text-red-900 border-red-400',
  Moderate: 'bg-amber-50 text-amber-900 border-amber-400',
  // The NWS also issues Minor and Unknown severities — a Heat Advisory is
  // often one of them — which previously fell through to an unstyled grey box.
  Minor: 'bg-amber-50 text-amber-900 border-amber-400',
  Unknown: 'bg-surface-sunken text-fg border-line',
};

const ZipForm = ({ initial, onSaved, onCancel }) => {
  const [zip, setZip] = useState(initial || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await dashboardAPI.savePreferences({ weather_zip: zip });
      toast.success('Weather location updated');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that ZIP code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="weather-zip-form">
      <Input value={zip} onChange={(e) => setZip(e.target.value)} maxLength={5}
        inputMode="numeric" placeholder="ZIP" className="h-8 w-24"
        aria-label="Weather ZIP code" data-testid="weather-zip-input" />
      <Button size="sm" className="h-8" onClick={save} disabled={saving}
        data-testid="weather-zip-save">Save</Button>
      {onCancel && (
        <Button size="sm" variant="ghost" className="h-8" onClick={onCancel}
          data-testid="weather-zip-cancel">Cancel</Button>
      )}
    </div>
  );
};

export const WeatherWidget = ({ data, onRefresh }) => {
  const [editing, setEditing] = useState(false);

  if (!data?.available) {
    return (
      <div className="space-y-2.5" data-testid="weather-unavailable">
        <p className="text-sm text-fg-muted">
          {data?.reason === 'no_zip'
            ? 'Set a ZIP code to see local conditions and weather alerts.'
            : 'Weather is unavailable right now.'}
        </p>
        <ZipForm initial={data?.zip} onSaved={() => { setEditing(false); onRefresh(); }} />
      </div>
    );
  }

  const now = data.current;
  const today = (data.forecast || [])[0];

  return (
    <div className="space-y-3" data-testid="weather-body">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-fg-muted flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 shrink-0" />{data.place || data.zip}
          </p>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className="text-3xl font-bold text-fg" data-testid="weather-temp">
              {now?.temperature_f ?? today?.temperature ?? '—'}°
            </span>
            <span className="text-sm text-fg-muted truncate">
              {now?.description || today?.short || ''}
            </span>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-fg-muted shrink-0"
          aria-label="Change weather location" onClick={() => setEditing((v) => !v)}
          data-testid="weather-edit-zip">
          <Pencil className="w-3.5 h-3.5" />
        </Button>
      </div>

      {editing && (
        <ZipForm initial={data.zip} onSaved={() => { setEditing(false); onRefresh(); }}
          onCancel={() => setEditing(false)} />
      )}

      {now?.wind_mph != null && (
        <p className="text-xs text-fg-muted flex items-center gap-1">
          <Wind className="w-3 h-3" />{now.wind_mph} mph
          {now.humidity != null ? ` · ${now.humidity}% humidity` : ''}
        </p>
      )}

      {(data.alerts || []).length > 0 && (
        <div className="space-y-1.5" data-testid="weather-alerts">
          {data.alerts.map((a, i) => (
            <div key={i}
              className={`text-xs rounded border px-2 py-1.5 flex items-start gap-1.5
                ${SEVERITY_STYLE[a.severity] || 'bg-surface-sunken text-fg border-line'}`}
              data-testid={`weather-alert-${i}`}>
              <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
              <span className="min-w-0"><b>{a.event}</b>{a.headline ? ` — ${a.headline}` : ''}</span>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-1 pt-1 border-t border-line-subtle">
        {(data.forecast || []).slice(0, 3).map((p, i) => (
          <div key={i} className="flex items-baseline gap-2 text-xs min-w-0"
            data-testid={`weather-forecast-${i}`}>
            <span className="text-fg-muted w-16 shrink-0 truncate">{p.name}</span>
            <span className="text-fg shrink-0">{p.temperature}°{p.unit}</span>
            <span className="text-fg-muted flex-1 min-w-0 truncate text-right"
              title={p.short}>{p.short}</span>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-fg-muted">
        {data.degraded ? 'Showing the last data we have · ' : ''}National Weather Service
      </p>
    </div>
  );
};

export default WeatherWidget;
